/**
 * Mar.te RENTRI Base in omaggio per chi ha pagato RENTRI Suite.
 *
 * Chi arriva dal portale RENTRI o da altri software ha registri e xFIR prodotti prima del
 * passaggio alla Suite: RENTRI Base li conserva a norma. Qui l'utente ottiene il buono Stripe
 * monouso (100% sul primo anno) e il link diretto che lo applica al checkout di RENTRI Base.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Check, Copy, ExternalLink, Gift, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { endpoints, type BaseVoucher } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export const BASE_VOUCHER_QUERY_KEY = ["billing-base-voucher"] as const;

export function useBaseVoucher(enabled = true) {
  return useQuery({
    queryKey: BASE_VOUCHER_QUERY_KEY,
    queryFn: endpoints.billing.baseVoucher,
    enabled,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Buono da mostrare: eleggibile e non ancora usato (il buono emesso resta visibile fino all'uso). */
export function baseVoucherVisible(v: BaseVoucher | undefined): v is BaseVoucher {
  return !!v && v.eligible && v.status !== "redeemed";
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function BaseVoucherCard({
  compact = false,
  className,
  id,
}: {
  compact?: boolean;
  className?: string;
  id?: string;
}) {
  const qc = useQueryClient();
  const voucherQ = useBaseVoucher();
  const [copied, setCopied] = useState(false);

  const issueMut = useMutation({
    mutationFn: endpoints.billing.issueBaseVoucher,
    onSuccess: (data) => {
      qc.setQueryData(BASE_VOUCHER_QUERY_KEY, data);
      toast.success("Buono RENTRI Base pronto");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Impossibile emettere il buono"),
  });

  const v = voucherQ.data;
  const visible = !!v && v.eligible;

  // Arrivo dal banner (/abbonamento#rentri-base): la card compare dopo il caricamento, quindi scorre qui.
  useEffect(() => {
    if (!visible || !id || window.location.hash !== `#${id}`) return;
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [visible, id]);

  if (!v || !visible) return null;

  const redeemed = v.status === "redeemed";

  const onCopy = async () => {
    if (!v.code) return;
    const ok = await copyToClipboard(v.code);
    if (ok) {
      setCopied(true);
      toast.success("Codice copiato");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Copia non riuscita: seleziona e copia il codice a mano");
    }
  };

  return (
    <Card
      id={id}
      className={cn(
        "scroll-mt-24 border-emerald-200/80 bg-emerald-50/40 shadow-sm dark:border-emerald-900 dark:bg-emerald-950/20",
        className,
      )}
    >
      <CardHeader className={compact ? "pb-3" : undefined}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Gift className="h-5 w-5 text-emerald-700" />
              Mar.te RENTRI Base in omaggio
            </CardTitle>
            <CardDescription className="mt-1">
              Hai già RENTRI Suite: il primo anno di RENTRI Base è gratis.{" "}
              <strong className="text-foreground">Risparmi {v.savings_label}</strong> ({v.price_label}
              /anno).
            </CardDescription>
          </div>
          <Badge variant={redeemed ? "secondary" : "success"} className="shrink-0">
            {redeemed ? "Buono utilizzato" : `Offerta valida fino al ${v.deadline}`}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!compact && (
          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <Archive className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
            <span>
              Se arrivi dal portale RENTRI o da un altro software, i registri e gli xFIR prodotti prima
              del passaggio a RENTRI Suite vanno comunque conservati a norma. Con RENTRI Base li scarichi
              dal RENTRI o li carichi dal browser e li mandi in conservazione presso Quality and Safety,
              conservatore qualificato AgID.
            </span>
          </p>
        )}

        {redeemed ? (
          <p className="text-sm text-emerald-800 dark:text-emerald-300">
            Hai già attivato RENTRI Base con il buono. Accedi su{" "}
            <a href={v.base_app_url} target="_blank" rel="noreferrer" className="underline">
              {v.base_app_url.replace(/^https?:\/\//, "")}
            </a>
            .
          </p>
        ) : v.code && v.link ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">Il tuo codice:</span>
              <code className="rounded-md border bg-background px-3 py-1.5 font-mono text-base font-semibold tracking-wider">
                {v.code}
              </code>
              <Button size="sm" variant="outline" onClick={onCopy}>
                {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
                {copied ? "Copiato" : "Copia"}
              </Button>
            </div>
            <Button asChild className="w-full sm:w-auto">
              <a href={v.link} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Apri RENTRI Base con lo sconto applicato
              </a>
            </Button>
            <p className="text-xs text-muted-foreground">
              Il link applica il buono da solo al momento del pagamento (importo 0 €). In alternativa
              inserisci il codice nel campo «codice promozionale» del pagamento su RENTRI Base. Il buono
              è personale, monouso e valido fino al {v.deadline}; dal rinnovo si applica il listino di
              RENTRI Base ({v.price_label}/anno), disdicibile in qualsiasi momento.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <Button onClick={() => issueMut.mutate()} disabled={issueMut.isPending}>
              {issueMut.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Gift className="mr-2 h-4 w-4" />
              )}
              Ottieni il buono gratuito
            </Button>
            <p className="text-xs text-muted-foreground">
              Riceverai un codice personale e un link diretto a RENTRI Base con lo sconto già applicato.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
